import { useEffect, useState, type ReactNode } from 'react';
import QRCode from 'qrcode';
import { api, ErrorApi, type Marca, type SesionCreada } from './api';
import { AvisoError, Boton, Skeleton, Tarjeta } from './ui';

/**
 * Face ID: configurar la pantalla de verificación, y probarla.
 *
 * ── POR QUÉ LAS DOS MITADES VIVEN JUNTAS ────────────────────────────────────────
 *
 * Configurar sin poder probar es pedirle a alguien que elija colores a ciegas para una
 * pantalla que **él nunca va a ver**: la ve una persona que no es cliente nuestro, en un
 * teléfono que no es el suyo, y que si algo falla no llama a soporte — se va. El cliente se
 * enteraría meses después, y solo de que "no convierte".
 *
 * Por eso el botón de prueba está al lado del formulario y no en otra página.
 */

/** El acento del Figma. No es un fallback triste: es un color que funciona. */
const ACENTO_DEFECTO = '#3332f2';
const ACENTO2_DEFECTO = '#6b5cff';

const MARCA_VACIA: Marca = { logoUrl: null, acento: null, acento2: null, tipografia: null };

function Etiqueta({ children }: { children: ReactNode }) {
  return <label className="text-[13px] font-medium">{children}</label>;
}

function Ayuda({ children }: { children: ReactNode }) {
  return (
    <p className="mt-1 text-[11.5px] leading-[1.45]" style={{ color: 'var(--text-3)' }}>
      {children}
    </p>
  );
}

const ENTRADA =
  'w-full rounded-lg px-3 py-2 text-[13px] outline-none transition placeholder:opacity-50';

const estiloEntrada = {
  background: 'var(--surface-2)',
  border: '1px solid var(--border-2)',
  color: 'var(--text)',
};

/**
 * Un color de la marca.
 *
 * El botón de limpiar no dice "Borrar": dice **"Usar el de Valídate"**. Es lo que realmente
 * pasa, y además le explica el `null` sin usar la palabra `null`.
 */
function CampoColor({
  label,
  valor,
  defecto,
  onCambio,
  ayuda,
}: {
  label: string;
  valor: string | null;
  defecto: string;
  onCambio: (v: string | null) => void;
  ayuda: ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <Etiqueta>{label}</Etiqueta>
        {valor !== null ? (
          <button
            type="button"
            onClick={() => onCambio(null)}
            className="text-[11.5px] underline-offset-2 hover:underline"
            style={{ color: 'var(--text-3)' }}
          >
            Usar el de Valídate
          </button>
        ) : null}
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <input
          type="color"
          aria-label={label}
          value={valor ?? defecto}
          onChange={(e) => onCambio(e.target.value)}
          className="h-[38px] w-11 shrink-0 cursor-pointer rounded-lg bg-transparent p-1"
          style={{ border: '1px solid var(--border-2)' }}
        />
        <input
          type="text"
          value={valor ?? ''}
          placeholder={`${defecto} (el de Valídate)`}
          spellCheck={false}
          onChange={(e) => onCambio(e.target.value.trim() === '' ? null : e.target.value.trim())}
          className={`tabular ${ENTRADA}`}
          style={estiloEntrada}
        />
      </div>
      <Ayuda>{ayuda}</Ayuda>
    </div>
  );
}

/**
 * La vista previa.
 *
 * Pinta **texto blanco sobre el acento elegido**, que es exactamente lo que hace la pantalla
 * real. Si el cliente eligió un amarillo, aquí lo ve ilegible antes de guardarlo — y el ojo
 * convence más rápido que cualquier aviso.
 *
 * La regla del contraste (`contrastaConBlanco`) NO se importa aquí a propósito. Vive en el
 * core, la aplica la pantalla y la API la usa para avisar; una tercera copia en el panel sería
 * una tercera cosa que puede divergir. Aquí el juez es el ojo, y el veredicto formal lo da el
 * servidor al guardar.
 */
function Vista({ m }: { m: Marca }) {
  const acento = m.acento ?? ACENTO_DEFECTO;
  const acento2 = m.acento2 ?? ACENTO2_DEFECTO;

  return (
    <div
      className="grid place-items-center rounded-xl p-5"
      style={{ background: `linear-gradient(150deg, ${acento}, ${acento2})` }}
    >
      <div
        className="w-[196px] rounded-[20px] p-4"
        style={{
          background: '#fff',
          fontFamily: m.tipografia !== null ? `${m.tipografia}, system-ui, sans-serif` : 'system-ui',
        }}
      >
        <div className="grid h-7 place-items-start">
          {m.logoUrl !== null && m.logoUrl !== '' ? (
            // Si la URL es mala, el navegador pinta el icono roto y ya está: es la verdad, y
            // es justo lo que la persona vería. Taparlo con un logo falso sería mentir en el
            // único sitio donde el cliente puede darse cuenta a tiempo.
            <img src={m.logoUrl} alt="" className="h-7 max-w-[120px] object-contain" />
          ) : (
            <span className="text-[13px] font-bold" style={{ color: '#101114' }}>
              Tu logo
            </span>
          )}
        </div>

        <p className="mt-3 text-[13px] font-semibold leading-tight" style={{ color: '#101114' }}>
          Verifica tu identidad
        </p>
        <p className="mt-1 text-[10.5px] leading-snug" style={{ color: '#6b6f76' }}>
          Vas a necesitar tu documento y la cámara.
        </p>

        <div
          className="mt-3 rounded-lg py-2 text-center text-[11.5px] font-semibold"
          style={{ background: acento, color: '#fff' }}
        >
          Empezar
        </div>
      </div>
    </div>
  );
}

function Configuracion() {
  const [m, setM] = useState<Marca | null>(null);
  const [avisos, setAvisos] = useState<string[]>([]);
  const [error, setError] = useState<ErrorApi | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);

  useEffect(() => {
    api
      .marca()
      .then((r) => {
        setM(r.marca);
        setAvisos(r.advertencias);
      })
      .catch((e: unknown) => {
        setM(MARCA_VACIA);
        if (e instanceof ErrorApi) setError(e);
      });
  }, []);

  function cambiar(parche: Partial<Marca>) {
    setM((v) => (v === null ? v : { ...v, ...parche }));
    setGuardado(false);
  }

  async function guardar() {
    if (m === null) return;
    setGuardando(true);
    setError(null);
    try {
      const r = await api.guardarMarca(m);
      setM(r.marca);
      setAvisos(r.advertencias);
      setGuardado(true);
    } catch (e: unknown) {
      if (e instanceof ErrorApi) setError(e);
    } finally {
      setGuardando(false);
    }
  }

  if (m === null) {
    return (
      <Tarjeta className="p-5">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="mt-4 h-9 w-full" />
        <Skeleton className="mt-3 h-9 w-full" />
        <Skeleton className="mt-3 h-40 w-full" />
      </Tarjeta>
    );
  }

  return (
    <Tarjeta className="p-5">
      <h2 className="text-[15px] font-semibold">Configuración</h2>
      <p className="mt-1 text-[12.5px]" style={{ color: 'var(--text-2)' }}>
        Se guarda una vez y se copia a cada verificación que crees. Lo que mandes en el POST de
        una sesión gana sobre esto, por si necesitas una marca distinta por campaña.
      </p>

      <div className="mt-5 flex flex-col gap-4">
        <div>
          <div className="flex items-center justify-between gap-2">
            <Etiqueta>Logo</Etiqueta>
            {m.logoUrl !== null ? (
              <button
                type="button"
                onClick={() => cambiar({ logoUrl: null })}
                className="text-[11.5px] underline-offset-2 hover:underline"
                style={{ color: 'var(--text-3)' }}
              >
                Quitar
              </button>
            ) : null}
          </div>
          <input
            type="url"
            value={m.logoUrl ?? ''}
            placeholder="https://tuempresa.com/logo.svg"
            spellCheck={false}
            onChange={(e) =>
              cambiar({ logoUrl: e.target.value.trim() === '' ? null : e.target.value.trim() })
            }
            className={`mt-1.5 ${ENTRADA}`}
            style={estiloEntrada}
          />
          <Ayuda>
            Tiene que servirse por <b>https</b>. Un recurso http dentro de una página https lo
            bloquea el navegador sin decir nada, y el logo simplemente no aparece.
          </Ayuda>
        </div>

        <CampoColor
          label="Color principal"
          valor={m.acento}
          defecto={ACENTO_DEFECTO}
          onCambio={(v) => cambiar({ acento: v })}
          ayuda={
            <>
              Lleva el texto blanco del botón encima. Míralo en la vista previa: si no se lee
              aquí, tampoco se lee a plena luz del sol — que es donde la gente escanea su DNI.
            </>
          }
        />

        <CampoColor
          label="Color secundario"
          valor={m.acento2}
          defecto={ACENTO2_DEFECTO}
          onCambio={(v) => cambiar({ acento2: v })}
          ayuda={<>Solo el degradado del fondo. No lleva texto encima, así que aquí vale todo.</>}
        />

        <div>
          <div className="flex items-center justify-between gap-2">
            <Etiqueta>Tipografía</Etiqueta>
            {m.tipografia !== null ? (
              <button
                type="button"
                onClick={() => cambiar({ tipografia: null })}
                className="text-[11.5px] underline-offset-2 hover:underline"
                style={{ color: 'var(--text-3)' }}
              >
                Usar la de Valídate
              </button>
            ) : null}
          </div>
          <input
            type="text"
            value={m.tipografia ?? ''}
            placeholder="Inter (la de Valídate)"
            spellCheck={false}
            onChange={(e) =>
              cambiar({ tipografia: e.target.value.trim() === '' ? null : e.target.value.trim() })
            }
            className={`mt-1.5 ${ENTRADA}`}
            style={estiloEntrada}
          />
          <Ayuda>
            La pantalla <b>pide</b> esta fuente, no la descarga: el nombre entra tal cual en
            <code className="mx-1 opacity-80">{'{tipografía}, system-ui, sans-serif'}</code>. Si el
            teléfono de la persona no la tiene, cae a la del sistema en silencio — y no hay
            aviso que valga, porque desde aquí no se puede saber qué fuentes tiene un teléfono
            ajeno. Con la tuya propia, cuenta con que muchos van a ver la del sistema.
          </Ayuda>
        </div>
      </div>

      <div className="mt-5">
        <div className="mb-2 text-[12px] font-medium" style={{ color: 'var(--text-2)' }}>
          Así lo va a ver la persona
        </div>
        <Vista m={m} />
      </div>

      {/*
        Los avisos NO van en rojo, y no es cosmética.

        Lo que se guardó, se guardó: el PUT devolvió 200. Pintar esto como un error diría que
        algo falló, y el cliente volvería a intentarlo pensando que no se grabó. Es información:
        tu dato está a salvo, pero la pantalla va a usar el defecto.
      */}
      {avisos.length > 0 ? (
        <div
          className="mt-4 flex flex-col gap-2 rounded-lg px-4 py-3 text-[12.5px] leading-[1.5]"
          style={{ background: 'var(--warn-weak)', color: 'var(--warn)' }}
        >
          <div className="font-semibold">Guardado, pero esto no se va a aplicar</div>
          {avisos.map((a) => (
            <p key={a}>{a}</p>
          ))}
        </div>
      ) : null}

      {error !== null ? (
        <div className="mt-4">
          <AvisoError mensaje={error.message} reembolsado={error.reembolsado} />
        </div>
      ) : null}

      <div className="mt-5 flex items-center gap-3">
        <Boton variante="primario" onClick={() => void guardar()} disabled={guardando}>
          {guardando ? 'Guardando…' : 'Guardar'}
        </Boton>
        {guardado && avisos.length === 0 ? (
          <span className="text-[12.5px]" style={{ color: 'var(--ok)' }}>
            Guardado.
          </span>
        ) : null}
      </div>
    </Tarjeta>
  );
}

function Prueba() {
  const [documentoCompleto, setDocumentoCompleto] = useState('');
  const [documento, setDocumento] = useState('');
  const [s, setS] = useState<SesionCreada | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [error, setError] = useState<ErrorApi | null>(null);
  const [creando, setCreando] = useState(false);
  const [copiado, setCopiado] = useState(false);

  async function crear() {
    setCreando(true);
    setError(null);
    setCopiado(false);
    try {
      // Las claves vacías se OMITEN, no se mandan en blanco: si va `documento` sin
      // `documentoEnmascarado`, el servidor deriva la máscara solo (····+4 últimos).
      const completo = documentoCompleto.trim();
      const enmascarado = documento.trim();
      const nueva = await api.crearSesion(
        completo === '' && enmascarado === ''
          ? undefined
          : {
              ...(enmascarado !== '' ? { documentoEnmascarado: enmascarado } : {}),
              ...(completo !== '' ? { documento: completo } : {}),
            },
      );
      setS(nueva);
      setQr(
        await QRCode.toDataURL(nueva.url, {
          margin: 1,
          width: 320,
          // Los ÚNICOS hexadecimales a mano de todo el panel, y con motivo: un QR en modo
          // oscuro, con los módulos claros sobre fondo negro, queda invertido — y muchos
          // lectores de teléfono no leen un QR invertido. Aquí el color no es estilo: es si
          // el código se escanea o no. Oscuro sobre blanco, siempre, en los dos temas.
          color: { dark: '#101114', light: '#ffffff' },
        }),
      );
    } catch (e: unknown) {
      if (e instanceof ErrorApi) setError(e);
    } finally {
      setCreando(false);
    }
  }

  async function copiar(url: string) {
    await navigator.clipboard.writeText(url).catch(() => undefined);
    setCopiado(true);
  }

  return (
    <Tarjeta className="p-5">
      <h2 className="text-[15px] font-semibold">Prueba</h2>
      <p className="mt-1 text-[12.5px]" style={{ color: 'var(--text-2)' }}>
        Crea un enlace real y ábrelo en tu teléfono. Es la misma sesión que recibiría un cliente
        tuyo — no una simulación, y por eso sirve.
      </p>

      <div className="mt-5">
        <Etiqueta>Documento (para consulta RENIEC)</Etiqueta>
        <input
          type="text"
          value={documentoCompleto}
          placeholder="12345678"
          spellCheck={false}
          onChange={(e) => setDocumentoCompleto(e.target.value)}
          className={`tabular mt-1.5 ${ENTRADA}`}
          style={estiloEntrada}
        />
        <Ayuda>
          El número completo, opcional. Viaja <b>solo</b> para que el servidor consulte el
          registro al crear la sesión y la foto oficial esté lista desde el principio — no se
          guarda en claro. Si lo mandas y dejas vacío el campo de abajo, la máscara se deriva
          sola de los últimos 4 dígitos.
        </Ayuda>
      </div>

      <div className="mt-4">
        <Etiqueta>Documento enmascarado (opcional)</Etiqueta>
        <input
          type="text"
          value={documento}
          placeholder="····7028"
          spellCheck={false}
          onChange={(e) => setDocumento(e.target.value)}
          className={`tabular mt-1.5 ${ENTRADA}`}
          style={estiloEntrada}
        />
        <Ayuda>
          Es lo que la portada muestra para que la persona sepa que el enlace es para ella.
          <b> Enmascáralo tú</b>: si nos mandaras el documento entero para que lo tapáramos
          nosotros, ese documento entero ya habría viajado y estaría en el log de alguien.
        </Ayuda>
      </div>

      <div className="mt-4">
        <Boton variante="primario" onClick={() => void crear()} disabled={creando}>
          {creando ? 'Creando…' : s === null ? 'Crear enlace de prueba' : 'Crear otro'}
        </Boton>
      </div>

      {error !== null ? (
        <div className="mt-4">
          <AvisoError mensaje={error.message} reembolsado={error.reembolsado} />
        </div>
      ) : null}

      {s !== null ? (
        <div className="mt-5 flex flex-col gap-4">
          <div className="flex justify-center rounded-xl p-4" style={{ background: '#fff' }}>
            {qr !== null ? (
              <img src={qr} alt="Código QR del enlace de verificación" className="h-44 w-44" />
            ) : (
              <Skeleton className="h-44 w-44" />
            )}
          </div>

          <p className="text-center text-[12.5px]" style={{ color: 'var(--text-2)' }}>
            Escanéalo con tu teléfono. Este es el traspaso que hace la API sola cuando la persona
            llega desde una computadora: la cámara buena está en el bolsillo, no en el escritorio.
          </p>

          <div className="flex items-center gap-2">
            <input
              readOnly
              value={s.url}
              onFocus={(e) => e.currentTarget.select()}
              className={`${ENTRADA} text-[11.5px]`}
              style={estiloEntrada}
            />
            <Boton onClick={() => void copiar(s.url)}>{copiado ? 'Copiado' : 'Copiar'}</Boton>
          </div>

          <div className="text-[11.5px]" style={{ color: 'var(--text-3)' }}>
            Caduca el{' '}
            {new Date(s.expiraEn).toLocaleString('es-PE', {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
            . El enlace lleva el token dentro: quien lo tenga, entra. No lo pegues en un chat de
            equipo ni en un ticket — este de prueba tampoco, que es igual de real que los otros.
          </div>
        </div>
      ) : null}
    </Tarjeta>
  );
}

export function FaceId() {
  return (
    <main className="flex-1 overflow-auto p-8">
      <header className="mb-6">
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Face ID</h1>
        <p className="mt-1 max-w-[680px] text-[13px]" style={{ color: 'var(--text-2)' }}>
          La pantalla donde una persona demuestra que es quien dice ser. Tú no la vas a ver
          nunca en condiciones reales: la ve ella, en su teléfono, con una mano sujetando el
          documento. Por eso está el botón de prueba.
        </p>
      </header>

      <div className="grid items-start gap-6 xl:grid-cols-2">
        <Configuracion />
        <Prueba />
      </div>
    </main>
  );
}
